import { parseAdditionalData } from './osintDomainRelevance';

export function isOsintCoverageDiagnostic(finding) {
  const additionalData = parseAdditionalData(finding);
  const sourceType = String(additionalData.sourceType ?? additionalData.SourceType ?? '').trim().toLowerCase();
  const fingerprint = String(finding?.fingerprint ?? '').trim().toLowerCase();
  const title = String(finding?.title ?? '').trim().toLowerCase();

  return fingerprint.startsWith('osint:diagnostic:')
    || (sourceType === 'operational-diagnostic'
      && (fingerprint.startsWith('osint:') || title.startsWith('osint provider failed:')));
}

export function partitionOsintFindings(findings = []) {
  const coverageDiagnostics = [];
  const exposureFindings = [];

  for (const finding of Array.isArray(findings) ? findings : []) {
    if (isOsintCoverageDiagnostic(finding)) coverageDiagnostics.push(finding);
    else exposureFindings.push(finding);
  }

  return { exposureFindings, coverageDiagnostics };
}

export function dashboardOsintPresentation(counts = {}) {
  const breachRecords = Math.max(0, Number(counts.breachRecords) || 0);
  const exposedCredentials = Math.max(0, Number(counts.exposedCredentials) || 0);
  const exposedServices = Math.max(0, Number(counts.exposedServices) || 0);
  const totalExposureFindings = Math.max(0, Number(counts.totalOsintFindings ?? counts.total) || 0);
  const coverageDiagnostics = Math.max(0, Number(counts.coverageDiagnostics) || 0);
  const coveragePending = Boolean(counts.coveragePending);
  const coverageError = Boolean(counts.coverageError);
  const coverageIncomplete = coverageDiagnostics > 0 || coveragePending || coverageError;
  const coverageReason = coverageDiagnostics > 0
    ? `${coverageDiagnostics} provider failure${coverageDiagnostics === 1 ? '' : 's'}`
    : coveragePending
      ? 'Provider coverage is still loading'
      : coverageError
        ? 'Provider coverage is unavailable'
        : 'Provider coverage completed';
  const exposureValue = value => coverageIncomplete && value === 0 ? '—' : value;
  const zeroNote = cleanText => coverageIncomplete ? `Unverified — ${coverageReason}` : cleanText;

  return {
    coverageIncomplete,
    coverageDiagnostics,
    coverageReason,
    breachRecords: {
      value: exposureValue(breachRecords),
      note: breachRecords > 0 ? `${breachRecords} records found` : zeroNote('No breach records found in the last 90 days'),
    },
    exposedCredentials: {
      value: exposureValue(exposedCredentials),
      note: exposedCredentials > 0 ? 'Requires rotation' : zeroNote('No exposed credentials detected'),
    },
    exposedServices: {
      value: exposureValue(exposedServices),
      note: exposedServices > 0 ? 'Review recommended' : zeroNote('No exposed services flagged'),
    },
    totalExposureFindings: {
      value: totalExposureFindings,
      note: coverageIncomplete
        ? `${totalExposureFindings} verified exposure finding${totalExposureFindings === 1 ? '' : 's'} · ${coverageReason}`
        : totalExposureFindings > 0
          ? 'From external intelligence'
          : 'External intelligence scan completed with no findings',
    },
  };
}
export function coverageDiagnosticSummary(finding) {
  const additionalData = parseAdditionalData(finding);
  const detail = finding?.detail ?? {};
  return {
    id: finding?.id ?? finding?.findingId ?? finding?.persistedFindingId ?? finding?.fingerprint,
    title: finding?.title || 'OSINT provider coverage unavailable',
    provider: additionalData.moduleId || detail.affectedComponent || finding?.module || 'OSINT provider',
    evidence: detail.evidence || finding?.description || 'The provider returned an unsuccessful result.',
    remediation: detail.remediation || 'Verify provider credentials, entitlement, rate limits, and network access, then rerun the module.',
    scanJobId: additionalData.scanJobId || '',
  };
}
